import { RpcError } from '@protobuf-ts/runtime-rpc';
import { merge } from 'lodash';
import { Platform } from '@fiverr-private/bianka_js';
import { globalEventBus } from '@fiverr-private/futile';
import stats from '../../../stats/emitter';
import logger from '../../../logger/emitter';
import isDev from '../../../helpers/isDev';
import { BIANKA_EVENT_NAME } from '../../helpers/eventName';
import { formatActivityRequest, formatBusinessRequest, formatEventMetricName } from '../../helpers/format';
import { ACTIONS } from '../../helpers/actionTypes';
import {
    BiankaServerError,
    BiankaClientError
} from './error';
import { METRICS } from './constants';

/**
 * The metrics prefix to use when sending metrics, set by the running service.
 */
let metricPrefix;

/**
 * Executes a give grpc call while tracking its result.
 * @param grpcCall - The GRPC call to trigger
 * @param metricName - The metric name to track with.
 * @return {Promise<void>}
 */
const executeGRPC = async(grpcCall, metricName) => {
    const start = Date.now();

    try {
        const call = grpcCall();
        const [status, response] = await Promise.all([call.status, call.response]);

        if (response.error) {
            stats.time(metricPrefix, `${metricName}.failure.${response.error.type}`, Date.now() - start);
            logger.warn(new BiankaServerError(response.error, status.code));
        } else {
            stats.time(metricPrefix, `${metricName}.success`, Date.now() - start);
        }
    } catch (error) {
        const status = error instanceof RpcError ? error.code : 'UNKNOWN';

        stats.time(metricPrefix, `${metricName}.failure.${status}`, Date.now() - start);

        logger.warn(new BiankaClientError(error, status));
    }
};

/**
 * Reports an Activity event.
 * @param {BiankaClientAPI} client - The Bianka client API differ by each platform Node/Browser.
 * @param {ReportActivityOptions} options
 * @return {Promise<void>}
 */
export const reportActivity = (client, options) => executeGRPC(() => {
    const request = formatActivityRequest(options);
    return client.reportActivity(request, { meta: client.formatHeaders() });
}, `${METRICS.ACTIVITY}.${formatEventMetricName(options.type)}`);

/**
 * Reports a Business event.
 * @param {BiankaClientAPI} client - The Bianka client API differ by each platform Node/Browser.
 * @param {ReportBusinessOptions} options
 * @return {Promise<void>}
 */
export const reportBusiness = (client, options) => executeGRPC(() => {
    const request = formatBusinessRequest(options);
    return client.reportBusiness(request);
}, `${METRICS.BUSINESS}.${formatEventMetricName(options.type)}`);

/**
 * The default enricher to use when no enricher was set.
 * Will warn if used without setting custom enricher about missing data.
 * @return {{}}
 */
const defaultEnricher = () => {
    logger.error('No enricher was set for Bianka listener, this means that no additional enrichment will be added to the report.');

    return {};
};

export class BiankaListener {
    constructor({ client, facility, mode, pageName }) {
        this.client = client;
        this.facility = facility;
        this.pageName = pageName;
        this.mode = mode;
        this.reportEnricher = defaultEnricher;

        metricPrefix = `services.${this.facility}.bianka`;
    }

    report(reportOptions) {
        const dev = isDev(this.mode);

        if (dev) {
            logger.info('Bianka:', merge(this.reportEnrichment, reportOptions));
            return;
        }

        const { biankaActionType: type, ...actionArgs } = merge(this.reportEnrichment, reportOptions, { metadataId: this.client.generateUUID() });

        switch (type) {
            case ACTIONS.ACTIVITY: {
                reportActivity(this.client, actionArgs);
                break;
            }
            case ACTIONS.BUSINESS: {
                reportBusiness(this.client, actionArgs);
                break;
            }
            default: {
                logger.warn(`Bianka API does not support action: ${type}, supported types are: ${Object.values(ACTIONS)}`);
            }
        }
    }

    get reportEnrichment() {
        const {
            userId,
            uid,
            locale,
            contextId,
            hostname,
            isTouch = false,
            page = {},
            ...customEnrichment
        } = this.reportEnricher();

        return {
            ...userId && { user: { id: userId } },
            ...locale && { locale: { id: { value: locale } } },
            page: merge({ name: this.pageName }, page),
            platform: isTouch ? Platform.MOBILE_WEB : Platform.WEB,
            hostname,
            contextId,
            uid,
            ...customEnrichment
        };
    }

    setEnricher(enricher) {
        this.reportEnricher = enricher;
    }
}

/**
 * Creates a Bianka reporting API listener.
 * @param options
 * @param {BiankaClientAPI} client
 * @param uuidGenerator
 * @return {BiankaListener}
 */
export const createBiankaListener = (options, client) => {
    if (globalEventBus.has(BIANKA_EVENT_NAME)) {
        logger.warn(new Error(`OBS Duplicate registering attempted to ${BIANKA_EVENT_NAME} bus`));
        return;
    }

    const listener = new BiankaListener({ ...options, client });

    globalEventBus.on(BIANKA_EVENT_NAME, listener.report.bind(listener));

    // Should return the BiankaListener class/object which has the setEnrichment method
    return listener;
};
